import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isValidUUID } from "@/lib/utils";
import { useMobile } from "@/hooks/useMobile";
import SelectDocumentStep from "./wizard/SelectDocumentStep";
import AddSignersStep from "./wizard/AddSignersStep";
import ReviewStep from "./wizard/ReviewStep";

interface SignatureRequestWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  unaId: string;
  onSuccess?: () => void;
  preSelectedDocumentId?: string;
}

export interface SignerData {
  id: string;
  name: string;
  email: string;
  role: string;
  signatureType: 'drawn' | 'typed' | 'uploaded';
  unaMemberId?: string | null;
  order: number;
}

export interface WizardData {
  selectedDocumentId: string;
  signers: SignerData[];
  signingOrder: 'parallel' | 'sequential';
  expiresAt: Date;
  reminderFrequency: 'none' | 'daily' | 'every_3_days' | 'weekly';
  message: string;
}

const steps = [
  { id: 1, name: "Select Document" },
  { id: 2, name: "Add Signers" },
  { id: 3, name: "Review & Send" },
];

export default function SignatureRequestWizard({
  open,
  onOpenChange,
  taskId,
  unaId,
  onSuccess,
  preSelectedDocumentId,
}: SignatureRequestWizardProps) {
  const isMobile = useMobile();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [wizardData, setWizardData] = useState<WizardData>({
    selectedDocumentId: preSelectedDocumentId || "",
    signers: [],
    signingOrder: 'sequential',
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    reminderFrequency: 'every_3_days',
    message: "",
  });

  useEffect(() => {
    if (preSelectedDocumentId) {
      updateWizardData({ selectedDocumentId: preSelectedDocumentId });
    }
  }, [preSelectedDocumentId]);

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!wizardData.selectedDocumentId;
      case 2:
        return wizardData.signers.length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: document } = await supabase
        .from('generated_documents')
        .select('file_url, document_name')
        .eq('id', wizardData.selectedDocumentId)
        .single();

      if (!document) throw new Error("Document not found");

      // Create signature request
      const { data: signatureRequest, error: requestError } = await supabase
        .from('signature_requests')
        .insert({
          una_id: unaId,
          document_id: wizardData.selectedDocumentId,
          task_id: taskId,
          created_by: user.id,
          signing_order: wizardData.signingOrder,
          message: wizardData.message,
          expires_at: wizardData.expiresAt.toISOString(),
          reminder_frequency: wizardData.reminderFrequency,
          status: 'pending',
        })
        .select()
        .single();

      if (requestError) throw requestError;

      // Create document_signers records
      const signersToInsert = wizardData.signers.map(signer => {
        const coercedUnaMemberId = signer.unaMemberId && isValidUUID(signer.unaMemberId) 
          ? signer.unaMemberId 
          : null;

        return {
          signature_request_id: signatureRequest.id,
          una_member_id: coercedUnaMemberId,
          signer_name: signer.name,
          signer_email: signer.email,
          signer_role: signer.role,
          signing_order: signer.order,
          signature_type: signer.signatureType,
          status: 'pending',
        };
      });

      const { error: signersError } = await supabase
        .from('document_signers')
        .insert(signersToInsert);

      if (signersError) throw signersError;

      // Log audit event
      await supabase.from('signature_audit_log').insert({
        signature_request_id: signatureRequest.id,
        event_type: 'request_created',
        event_data: { signers_count: wizardData.signers.length },
        actor_id: user.id,
      });

      // Send signature request emails to each signer
      for (const signer of wizardData.signers) {
        try {
          await supabase.functions.invoke('send-member-signature-request', {
            body: {
              signatureRequestId: signatureRequest.id,
              signerEmail: signer.email,
              signerName: signer.name,
            }
          });
        } catch (emailError) {
          console.error('Error sending signature email:', emailError);
        }
      }

      toast.success(`Signature request sent to ${wizardData.signers.length} signer(s)`);
      onSuccess?.();
      onOpenChange(false);
      
      // Reset wizard
      setCurrentStep(1);
      setWizardData({
        selectedDocumentId: "",
        signers: [],
        signingOrder: 'sequential',
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        reminderFrequency: 'every_3_days',
        message: "",
      });
    } catch (error: any) {
      console.error('Error creating signature request:', error);
      toast.error(error.message || 'Failed to create signature request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Signatures</DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        {isMobile ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Step {currentStep} of {steps.length}</span>
              <span className="text-sm text-muted-foreground">{steps[currentStep - 1].name}</span>
            </div>
            <Progress value={(currentStep / steps.length) * 100} className="h-2" />
          </div>
        ) : (
          <div className="flex items-center justify-between mb-6">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  currentStep >= step.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step.id}
                </div>
                <span className="ml-2 text-sm hidden sm:inline">{step.name}</span>
                {idx < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 1 && (
            <SelectDocumentStep
              taskId={taskId}
              selectedDocumentId={wizardData.selectedDocumentId}
              onSelect={(id) => updateWizardData({ selectedDocumentId: id })}
            />
          )}
          {currentStep === 2 && (
            <AddSignersStep
              unaId={unaId}
              documentId={wizardData.selectedDocumentId}
              taskId={taskId}
              signers={wizardData.signers}
              onUpdateSigners={(signers) => updateWizardData({ signers })}
            />
          )}
          {currentStep === 3 && (
            <ReviewStep wizardData={wizardData} />
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < steps.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Sending...' : 'Send Signature Request'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
